import { LOAD_ARTICLE, LOAD_ARTICLES } from "models/queries/article.queries";
import React, { Component } from "react";
import { Button, Form } from "react-bootstrap";
import { IArticle } from "sthinds.io-lib";
import GraphQLService from "network/graphql.service";
import { PATCH_ARTICLE } from "models/mutations/article.mutations";
import { Navigate, useParams } from "react-router-dom";
import MarkdownEditor from "../editor/markdown-editor.component";
import "../create/create-article.component.scss";
import { articlePath } from "../article-url";

interface IEditArticleComponentProps {
  articleID: string;
}
interface IEditArticleComponentState extends IArticle {
  redirect: boolean;
  saving: boolean;
  saveError: string | null;
}

class EditArticleComponent extends Component<
  IEditArticleComponentProps,
  IEditArticleComponentState
> {
  state: IEditArticleComponentState = {
    _id: "",
    title: "",
    subtitle: "",
    authorID: "",
    date: "",
    text: "",
    redirect: false,
    saving: false,
    saveError: null,
  };

  componentDidMount() {
    this.setState({
      _id: this.props.articleID,
      date: new Date().toDateString(),
    });

    GraphQLService.query({
      query: LOAD_ARTICLE,
      variables: {
        articleID: this.props.articleID,
      },
    }).then((result) => {
      this.setState(result.data.article);
    });
  }

  public updateArticle = () => {
    // TODO: enforce rule that only same author who created article can edit
    this.setState({ saving: true, saveError: null });

    GraphQLService.mutate({
      mutation: PATCH_ARTICLE,
      variables: {
        _id: this.state._id,
        patch: {
          title: this.state.title,
          subtitle: this.state.subtitle,
          authorID: this.state.authorID,
          date: this.state.date,
          text: this.state.text,
        },
      },
      // The article page is otherwise served from Apollo's cache immediately
      // after the redirect, so refresh both the post itself and the index.
      refetchQueries: [
        { query: LOAD_ARTICLE, variables: { articleID: this.state._id } },
        { query: LOAD_ARTICLES },
      ],
      awaitRefetchQueries: true,
    })
      .then(() => {
        // Back to the article rather than reloading the editor, so a save is
        // visibly a save.
        this.setState({ saving: false, redirect: true });
      })
      .catch((err) => {
        this.setState({
          saving: false,
          saveError: err?.message ?? "Could not save the article.",
        });
      });
  };

  handleCancel = () => {
    this.setState({ redirect: true });
  };

  render() {
    if (this.state.redirect) {
      return <Navigate to={articlePath(this.state)} replace />;
    }

    return (
      <div className="article-form">
        <Form onSubmit={(event) => event.preventDefault()}>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              onChange={(e) => {
                this.setState({
                  title: e.target.value,
                });
              }}
              type="text"
              value={this.state.title}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Subtitle</Form.Label>
            <Form.Control
              onChange={(e) => {
                this.setState({
                  subtitle: e.target.value,
                });
              }}
              type="text"
              value={this.state.subtitle}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Text</Form.Label>
            <MarkdownEditor
              value={this.state.text}
              onChange={(text) => this.setState({ text })}
            />
          </Form.Group>
          <div className="article-form-actions flex-row whole-width justify-end align-center">
            {this.state.saveError ? (
              <span className="article-form-error">{this.state.saveError}</span>
            ) : null}
            <Button
              variant="secondary"
              type="button"
              onClick={this.handleCancel}
            >
              Cancel
            </Button>
            {/* type="button": a native submit reloads the page mid-mutation. */}
            <Button
              variant="primary"
              type="button"
              onClick={this.updateArticle}
              disabled={
                this.state.saving ||
                !this.state.title?.trim() ||
                !this.state.text?.trim()
              }
            >
              {this.state.saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </Form>
      </div>
    );
  }
}

/** Route parameters, not the persisted Redux selection, identify the post. */
export default function EditArticleRoute(): JSX.Element {
  const { articleID } = useParams<{ articleID: string }>();
  if (!articleID) return <Navigate to="/" replace />;
  return <EditArticleComponent articleID={articleID} />;
}
